/**
 * Pure plan-diff producer per `04-` §12 + D-04.
 *
 * Takes a `Snapshot` and a `PlanResult` and emits an ordered list of
 * mutations describing what changes from the snapshot's current task dates.
 *
 * The adapter consumer (`event_bridge.ts`, Day 10) writes the diff back to
 * gantt via `gantt.batchUpdate` and fires the per-task events. Splitting the
 * diff producer from the gantt writer keeps the engine pure (`applyPlan`
 * imports nothing gantt-specific) and makes the diff trivially testable.
 *
 * Per D-04: the engine owns both `start` and `end`. The applier surfaces
 * both dates; the live gantt writer still owns the final write-back policy.
 */

import type {
  ConstraintType,
  PlanResult,
  ScheduleReason,
  Snapshot,
  TaskID,
  NormalizedTaskID
} from "../core/types";
import { toPublicScheduleReason } from "../core/ids";

export interface AppliedMutation {
  readonly normalizedTaskId: NormalizedTaskID;
  readonly taskId: TaskID;
  /** Snapshot-time dates — what the user sees before the apply. */
  readonly prev: { readonly start: Date; readonly end: Date };
  /** Engine-resolved dates — what the user will see after the apply. */
  readonly next: { readonly start: Date; readonly end: Date };
  /** What drove the new date — for event-arg derivation (D-18). */
  readonly reason: ScheduleReason;
  /** Resolved (post-default) constraint type. */
  readonly resolvedConstraint: ConstraintType;
}

/**
 * Build the mutation list. Walks `planResult.order` (toposort/reverse) so
 * the consumer can fire per-task events in the engine's resolution order
 * (D-17). Skips tasks not present in the snapshot defensively.
 *
 * `planResult.plans` is already sparse — `plan_unmemoized` only emits
 * entries whose dates differ from snapshot anchors. The applier preserves
 * that sparsity and additionally re-checks against snapshot.tasks dates so
 * a downstream caller passing a custom planResult (e.g. from a re-plan
 * after `onBeforeTaskAutoSchedule` cancellation) doesn't accidentally
 * emit no-op writes.
 */
export function applyPlan(snapshot: Snapshot, planResult: PlanResult): readonly AppliedMutation[] {
  const result: AppliedMutation[] = [];
  for (const taskId of planResult.order) {
    const scheduled = planResult.plans.get(taskId);
    if (!scheduled) continue;
    const task = snapshot.tasks.get(taskId);
    if (!task) continue;
    const prevStart = task.startDate;
    const prevEnd = task.endDate;
    if (
      scheduled.start.getTime() === prevStart.getTime() &&
      scheduled.end.getTime() === prevEnd.getTime()
    ) {
      continue;
    }
    result.push({
      normalizedTaskId: taskId,
      taskId: task.rawId,
      prev: { start: new Date(prevStart.getTime()), end: new Date(prevEnd.getTime()) },
      next: { start: scheduled.start, end: scheduled.end },
      reason: toPublicScheduleReason(snapshot, scheduled.reason),
      resolvedConstraint: scheduled.resolvedConstraint
    });
  }
  // `planResult.order` may omit tasks that were scheduled but not in the
  // toposort (e.g., summary-translated descendants when the iteration walked
  // only the scheduling-graph backbone). Re-walk plans to catch them.
  if (result.length < planResult.plans.size) {
    const emitted = new Set<NormalizedTaskID>(result.map(m => m.normalizedTaskId));
    for (const [taskId, scheduled] of planResult.plans) {
      if (emitted.has(taskId)) continue;
      const task = snapshot.tasks.get(taskId);
      if (!task) continue;
      const prevStart = task.startDate;
      const prevEnd = task.endDate;
      if (
        scheduled.start.getTime() === prevStart.getTime() &&
        scheduled.end.getTime() === prevEnd.getTime()
      ) {
        continue;
      }
      result.push({
        normalizedTaskId: taskId,
        taskId: task.rawId,
        prev: { start: new Date(prevStart.getTime()), end: new Date(prevEnd.getTime()) },
        next: { start: scheduled.start, end: scheduled.end },
        reason: toPublicScheduleReason(snapshot, scheduled.reason),
        resolvedConstraint: scheduled.resolvedConstraint
      });
    }
  }
  return result;
}
